// Widget runtime config resolution.
// - The admin API base URL comes from the script src's origin (e.g. http://localhost:3000)
//   OR from a `data-api` attribute if the customer wants to override.
// - Workspace ID always comes from `data-workspace`.
// - Optional `data-locale` overrides the auto-detected reporter locale (BCP-47 tag).

export interface WidgetConfig {
  workspaceId: string;
  apiBaseUrl: string;
  /** Origin serving this widget asset, used only for the #774 hosted recorder. */
  widgetBaseUrl: string;
  /** Optional BCP-47 locale override from the integration snippet. */
  locale: string | null;
  assistantEndpoint?: string;
}

export function resolveConfig(script: HTMLScriptElement): WidgetConfig | null {
  const workspaceId = script.dataset.workspace;
  if (!workspaceId) return null;
  const explicit = script.dataset.api;
  const apiBaseUrl = explicit
    ? explicit.replace(/\/$/, '')
    : (inferAdminBaseUrl(script.src) ?? 'http://localhost:3000');
  const locale = script.dataset.locale?.trim() || null;
  let widgetBaseUrl: string;
  try {
    widgetBaseUrl = new URL(script.src).origin;
  } catch {
    return null;
  }
  return {
    workspaceId,
    apiBaseUrl,
    widgetBaseUrl,
    locale,
    assistantEndpoint:
      script.dataset.assistantEndpoint === '/oncatch/assistant' ? '/oncatch/assistant' : undefined,
  };
}

function inferAdminBaseUrl(src: string): string | null {
  try {
    const u = new URL(src);
    // In prod the widget CDN is on widget.oncatch.app; admin is admin.oncatch.app.
    // For local dev both are on localhost (widget :5173, admin :3000) so we can't infer
    // from origin — the customer will paste data-api="http://localhost:3000" locally.
    if (u.hostname === 'localhost' || u.hostname === '127.0.0.1') return null;
    if (u.hostname.startsWith('widget.'))
      return `${u.protocol}//${u.hostname.replace('widget.', 'admin.')}`;
    return u.origin;
  } catch {
    return null;
  }
}
